<script setup lang="ts">
import { computed } from 'vue'

interface Option {
  optGroupLabel?: string
  optLabel?: string
  optValue?: string | number
  optIcon?: string
  optHref?: string
  optCounter?: number
  iconPosition?: 'left' | 'right'
  copyable?: boolean
  disabled?: boolean
  options?: Option[]
}

const props = withDefaults(defineProps<{
  title?: string
  value?: string | number | null
  options: Option[]
  unpadGrouped?: boolean
  showCounters?: boolean
  itemAlign?: 'center' | 'start' | 'end'
}>(), {
  value: null,
  unpadGrouped: false,
  showCounters: false,
})

const emit = defineEmits<{
  (e: 'click', option: Option): void
  (e: 'link-click'): void
}>()

const optionsObject = computed(() => {
  const hasGroupedOptions = props.options.some(
    (opt) => opt.optGroupLabel && opt.options,
  )
  let optionsToUse = props.options

  if (hasGroupedOptions) {
    optionsToUse = props.options.flatMap((currentGroup) => [
      { optGroupLabel: currentGroup.optGroupLabel },
      ...(currentGroup.options ?? []),
    ])
  }

  return { grouped: hasGroupedOptions, options: optionsToUse }
})

const optionsHaveIcon = computed(() => optionsObject.value.options.some((op) => op.optIcon))

const listItemClass = [
  'flex h-12 cursor-pointer items-center',
  'justify-between gap-2 px-4 py-3',
  'text-sm font-medium leading-5 tracking-[0.0125rem]',
  'text-grey-900 hover:bg-primary-500 hover:text-white',
  'active:bg-primary-600 active:text-white',
  'sm:h-12 sm:p-4 sm:text-base',
]

function isSelected(option: Option) {
  return !!option.optValue && props.value === option.optValue
}

function selectOption(option: Option) {
  emit('click', option)
}

function handleLinkClick() {
  emit('link-click')
}
</script>

<template>
  <ul class="flex flex-col overflow-auto bg-white [&::-webkit-scrollbar]:hidden">
    <span v-if="title" class="flex h-[3.25rem] items-center px-4 text-lg font-semibold text-grey-900" >
      {{ $t(title) }}
    </span>
    <template v-for="(option, key) in optionsObject.options">
      <span
        v-if="!!option.optGroupLabel"
        :key="`label-${key}`"
        class="flex h-12 items-end p-4 font-semibold text-grey-700 sm:h-12 sm:py-0 sm:text-xs"
      >
        {{ $t(option.optGroupLabel) }}
      </span>
      <li v-else :key="`item-${key}`">
        <!-- Link type option -->
        <a
          v-if="!!option.optHref"
          :class="[listItemClass, 'truncate', {
            'pointer-events-none cursor-default bg-grey-200 text-grey-500': option.disabled,
          }]"
          :href="option.optHref"
          target="_blank"
          @click.stop="handleLinkClick"
        >
          <sword-icon class="mx-1 size-5 flex-none" :name="option.optIcon" />
          <span class="flex min-w-px grow text-sm font-medium">
            {{ $t(option.optLabel ?? '') }}
          </span>
          <sword-icon class="ml-1 size-5 flex-none fill-grey-700" name="external-link" />
        </a>
        <!-- Regular type option -->
        <div
          v-else
          :class="[listItemClass, {
            'pointer-events-none bg-primary-100 font-medium text-grey-900': isSelected(option),
            'pointer-events-none cursor-default bg-grey-200 text-grey-500': option.disabled,
            'justify-center gap-1': itemAlign === 'center',
            'justify-start gap-1': itemAlign === 'start',
            'justify-end gap-1': itemAlign === 'end',
          }]"
          @click.prevent="selectOption(option)"
        >
          <sword-icon
            v-if="optionsHaveIcon && (!option.iconPosition || option.iconPosition === 'right')"
            class="size-5 flex-none"
            :name="option.optIcon"
          />
          <span
            class="flex min-w-px grow text-sm font-medium"
            :class="{ 'grow-0': itemAlign }"
          >
            <span class="truncate">
              {{ $t(option.optLabel ?? '') }}
            </span>
            <span
              v-if="showCounters && option.optCounter != undefined"
              class="flex before:whitespace-pre before:content-['\0020']"
            >
              ({{ option.optCounter }})
            </span>
          </span>
          <sword-icon
            v-if="option.copyable || (optionsHaveIcon && option.iconPosition === 'left')"
            class="size-5 flex-none"
            :name="option.copyable ? 'copy' : option.optIcon"
          />
        </div>
      </li>
    </template>
  </ul>
</template>
